import { generateClient } from 'aws-amplify/api';

import {
  createWebhook,
  deleteWebhook,
  updateWebhook,
} from './API/graphql/mutations';
import { jwks, webhookEvents, webhooks } from './API/graphql/queries';
import {
  CreateWebhookMutationVariables,
  DeleteWebhookMutationVariables,
  UpdateWebhookMutationVariables,
  WebhookNotificationResult,
} from './API/types';
import { ParsedResponse } from './util';

/** Merchant-owned webhook endpoint returned by the authenticated API. */
export type MerchantWebhook = {
  endpoint: string;
  isActive: boolean;
  name: string;
};

/**
 * One flat delivery attempt. `id` is unique per HTTP attempt and is the
 * detail-lookup key; `eventId` is the public `pt*_webh_*` Webhook Event ID
 * shared by every retry of the same destination delivery. Legacy attempts
 * recorded before public IDs existed have no `eventId`.
 */
export type WebhookDeliveryAttempt = {
  endpoint: string;
  error?: string;
  event: string;
  eventId?: string;
  finishedAt: string;
  id: string;
  request: string;
  response?: string;
  result: WebhookNotificationResult;
  retryCount: number;
  startedAt: string;
  statusCode?: number;
};

/** Public Ed25519 material used by a receiver to verify webhook signatures. */
export type PublicWebhookVerificationKey = {
  alg: string;
  crv: string;
  kid: string;
  kty: string;
  use: string;
  x: string;
};

export type WebhookHistoryPage = {
  events: WebhookDeliveryAttempt[];
  lastEvaluatedKey?: string;
};

const publicKeyFields: ReadonlyArray<keyof PublicWebhookVerificationKey> = [
  'alg',
  'crv',
  'kid',
  'kty',
  'use',
  'x',
];

const getErrors = (error: unknown, fallback: string): string[] => {
  if (
    typeof error === 'object' &&
    error !== null &&
    'errors' in error &&
    Array.isArray(error.errors)
  ) {
    return error.errors.map(item =>
      typeof item === 'object' && item !== null && 'message' in item
        ? String(item.message)
        : fallback,
    );
  }
  return [fallback];
};

const isCompletePublicKey = (
  key: Partial<PublicWebhookVerificationKey> | null,
): key is PublicWebhookVerificationKey =>
  Boolean(
    key &&
      publicKeyFields.every(
        field => typeof key[field] === 'string' && key[field]?.length,
      ),
  );

const projectPublicKey = (
  key: PublicWebhookVerificationKey,
): PublicWebhookVerificationKey => ({
  alg: key.alg,
  crv: key.crv,
  kid: key.kid,
  kty: key.kty,
  use: key.use,
  x: key.x,
});

/**
 * List endpoints from the authenticated AppSync context. Ownership is never a
 * client variable, so a caller cannot select another merchant.
 */
export const list = async (
  endpoint?: string,
): Promise<ParsedResponse<MerchantWebhook[]>> => {
  try {
    const response = await generateClient().graphql({
      query: webhooks,
      variables: endpoint ? { endpoint } : undefined,
    });
    if (response.errors) {
      return { data: null, errors: response.errors.map(item => item.message) };
    }
    return {
      data: response.data.webhooks.filter(Boolean).map(item => ({
        endpoint: item!.endpoint,
        isActive: item!.is_active,
        name: item!.name,
      })),
      errors: null,
    };
  } catch (error) {
    return { data: null, errors: getErrors(error, 'Unable to load webhooks') };
  }
};

/** Create an endpoint for the authenticated merchant. */
export const create = async (
  variables: CreateWebhookMutationVariables,
): Promise<ParsedResponse<boolean>> => {
  try {
    const response = await generateClient().graphql({
      query: createWebhook,
      variables,
    });
    if (response.errors) {
      return { data: null, errors: response.errors.map(item => item.message) };
    }
    return { data: response.data.createWebhook, errors: null };
  } catch (error) {
    return { data: null, errors: getErrors(error, 'Unable to create webhook') };
  }
};

/** Rename or change the active state of an authenticated merchant endpoint. */
export const update = async (
  variables: UpdateWebhookMutationVariables,
): Promise<ParsedResponse<boolean>> => {
  try {
    const response = await generateClient().graphql({
      query: updateWebhook,
      variables,
    });
    if (response.errors) {
      return { data: null, errors: response.errors.map(item => item.message) };
    }
    return { data: response.data.updateWebhook, errors: null };
  } catch (error) {
    return { data: null, errors: getErrors(error, 'Unable to update webhook') };
  }
};

/** Delete an authenticated merchant endpoint without deleting retained history. */
export const remove = async (
  variables: DeleteWebhookMutationVariables,
): Promise<ParsedResponse<boolean>> => {
  try {
    const response = await generateClient().graphql({
      query: deleteWebhook,
      variables,
    });
    if (response.errors) {
      return { data: null, errors: response.errors.map(item => item.message) };
    }
    return { data: response.data.deleteWebhook, errors: null };
  } catch (error) {
    return { data: null, errors: getErrors(error, 'Unable to delete webhook') };
  }
};

/** List flat delivery attempts for the authenticated merchant. */
export const listEvents = async (variables: {
  endpoint?: string;
  id?: string;
  last_evaluated_key?: string;
  limit?: number;
  result?: WebhookNotificationResult;
}): Promise<ParsedResponse<WebhookHistoryPage>> => {
  try {
    const response = await generateClient().graphql({
      query: webhookEvents,
      variables,
    });
    if (response.errors) {
      return { data: null, errors: response.errors.map(item => item.message) };
    }
    return {
      data: {
        events: response.data.webhookEvents.events
          .filter(Boolean)
          .map(item => ({
            endpoint: item!.endpoint,
            error: item!.error ?? undefined,
            event: item!.event,
            eventId: item!.event_id ?? undefined,
            finishedAt: item!.finished_at,
            id: item!.id,
            request: item!.request,
            response: item!.response ?? undefined,
            result: item!.result,
            retryCount: item!.retry_count ?? 0,
            startedAt: item!.started_at,
            statusCode: item!.status_code ?? undefined,
          })),
        lastEvaluatedKey:
          response.data.webhookEvents.last_evaluated_key ?? undefined,
      },
      errors: null,
    };
  } catch (error) {
    return {
      data: null,
      errors: getErrors(error, 'Unable to load webhook delivery history'),
    };
  }
};

/**
 * Retrieve public-only verification material. The explicit projection ensures
 * future API fields, especially private key material, never reach the UI.
 */
export const getVerificationKeys = async (): Promise<
  ParsedResponse<PublicWebhookVerificationKey[]>
> => {
  try {
    const response = await generateClient().graphql({
      query: jwks,
    });
    if (response.errors) {
      return { data: null, errors: response.errors.map(item => item.message) };
    }
    return {
      data: (response.data.jwks?.keys ?? []).flatMap(key =>
        isCompletePublicKey(key) ? [projectPublicKey(key)] : [],
      ),
      errors: null,
    };
  } catch (error) {
    return {
      data: null,
      errors: getErrors(error, 'Unable to retrieve webhook verification keys'),
    };
  }
};
